import classNames from "classnames";
import { CompositeDecorator, ContentBlock, ContentState, convertFromRaw, convertToRaw, DraftEditorCommand, Editor, EditorState, Modifier, RawDraftContentState, RichUtils } from 'draft-js';
import { stateToHTML } from "draft-js-export-html";
import Immutable from "immutable";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Justify, ListOl, ListUl, TextCenter, TextLeft, TextRight } from "react-bootstrap-icons";
import { AppProps } from "../AppProps";
import { ThemeContext } from "../UserTheme";
import { Dropdown, DropdownItemProps } from "./RichTextDropdown";
import { LinkEditor, LinkEntity } from "./RichTextLink";

export declare interface RichTextEditorRef {
  getText(): string;
  setText(text: string): void;
  getContentState(): RawDraftContentState;
  setContentState(rawContentState: RawDraftContentState): void;
  isEmpty(): boolean;
}

declare interface RichTextEditorProps {
  simple?: boolean;
}

export declare type TextAlignment = "left" | "right" | "center" | "justify";

// Using Microsoft Word colors combined with USF theme colors
export const textColors = ["DEFAULT", "#C00000", "#FF0000", "#FFC000", "#FFFF00", "#92D050", "#00B050", "#00B0F0", "#0070C0", "#006747", "#CFC493", "#EDEBD1",
  "#005432", "#DBE442", "#9CCB3B", "#009374", "#80B0A6", "#006484", "#CAD2D8", "#7E96A0", "#466069"];

export const highlightColors = ["DEFAULT", "#FFFF00", "#00FFFF", "#00FF00", "#FF00FF", "#FF0000"];

const findLinkEntities = (contentBlock: ContentBlock, callback: (start: number, end: number) => void, contentState: ContentState) => {
  contentBlock.findEntityRanges(
    character => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === "LINK"
      );
    },
    callback
  );
};

export const RichTextEditor = forwardRef(({ simple }: RichTextEditorProps, ref) => {
  simple = simple ?? false;
  const decorator = new CompositeDecorator([
    {
      strategy: findLinkEntities,
      component: LinkEntity
    }
  ]);

  const [editorState, setEditorState] = useState(
    () => EditorState.createEmpty(decorator)
  );
  const editorRef = useRef<Editor>(null);

  const [optionsDropdownOpen, setOptionsDropdownOpen] = useState(false);
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false);
  const [highlightColorPickerOpen, setHighlightColorPickerOpen] = useState(false);
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);

  const [defaultTextColor, setDefaultTextColor] = useState("#111827");

  const { theme } = useContext(ThemeContext);

  // This ref and the width are needed for responsive UI
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    updateDimensions();

    window.addEventListener('resize', updateDimensions);
    return () =>
      window.removeEventListener('resize', updateDimensions);
  }, [])
  const updateDimensions = () => {
    setWidth(containerRef.current!.offsetWidth);
  }

  const textColorMap: { [key: string]: { color: string } } = {};

  textColors.forEach((color) => {
    textColorMap[`COLOR_${color}`] = {
      color: (color === "DEFAULT") ? defaultTextColor : color
    };
  });

  const highlightColorMap: { [key: string]: { backgroundColor: string } } = {};

  highlightColors.forEach((color) => {
    highlightColorMap[`HIGHLIGHT_${color}`] = {
      backgroundColor: color
    }
  });

  // Custom styles that are not native to Draft
  const styleMap = {
    "STRIKETHROUGH": {
      textDecoration: "line-through"
    },
    ...textColorMap,
    ...highlightColorMap
  };

  useEffect(() => {
    if (theme === "dark") {
      setDefaultTextColor("#FFF"); // Taken from Tailwind prose invert
    }
    else if (theme === "light") {
      setDefaultTextColor("#111827"); // Taken from Tailwind prose
    }
  }, [theme]);

  // Will be used as Editor's blockStyleFn prop to allow for text alignment toggling
  const getBlockStyle = (block: ContentBlock) => {
    const blockData = block.getData();

    const textAlignment = blockData.get("textAlignment");

    switch (textAlignment) {
      case "left":
        return "align-left";
      case "center":
        return "align-center";
      case "right":
        return "align-right";
      case "justify":
        return "align-justify";
      default:
        return "align-left";
    }
  };

  const getCurrentlySelectedBlock = (editorState: EditorState) => {
    const selection = editorState.getSelection();
    const startKey = selection.getStartKey();
    let endKey = selection.getEndKey();
    const content = editorState.getCurrentContent();
    let target = selection;

    if (startKey !== endKey && selection.getEndOffset() === 0) {
      const blockBefore = content.getBlockBefore(endKey);

      if (blockBefore) {
        endKey = blockBefore.getKey();
        target = target.merge({
          anchorKey: startKey,
          anchorOffset: selection.getStartOffset(),
          focusKey: endKey,
          focusOffset: blockBefore.getLength(),
          isBackward: false
        });
      }
    }

    const hasAtomicBlock = content.getBlockMap()
      .skipWhile((_, k) => k !== startKey)
      .takeWhile((_, k) => k !== endKey)
      .some(v => v!.getType() === "atomic");

    const currentBlock = content.getBlockForKey(startKey);

    return {
      content,
      currentBlock,
      hasAtomicBlock,
      target
    };
  };

  /* In Draft JS, we are allowed to define custom styles that are not default Draft styles.
   * These styles go in the customStyleMap Editor prop (styleMap object we have here).
   * Certain styles should only have one style active at a time, like text color and highlight color for example.
   * If we apply a yellow highlight and then apply a blue highlight, we would expect only the blue highlight to be active in the editor.
   * We do this by prefixing custom styles that should only have one active at a time over a selection. The text color style prefix is "COLOR_",
   * while the highlight color style prefix is "HIGHLIGHT_". This function ensures that only one style of a given prefix is active at a time over a selection.
   */
  const togglePrefixedInlineStyle = (editorState: EditorState, prefix: string, toggledStyle: string) => {
    const selection = editorState.getSelection();

    // To allow only one text color at a time, we must turn off all other applied text color styles.
    const nextContentState = Object.keys(styleMap).reduce((contentState, style) => {
      // We have multiple custom styles, so we only want to toggle off other text color styles, prefixed by "COLOR_"
      if (!style.startsWith(prefix)) {
        return contentState;
      }
      return Modifier.removeInlineStyle(contentState, selection, style);
    }, editorState.getCurrentContent());

    let nextEditorState = EditorState.push(
      editorState,
      nextContentState,
      "change-inline-style"
    );

    const currentStyle = editorState.getCurrentInlineStyle();

    if (selection.isCollapsed()) {
      nextEditorState = currentStyle.reduce((state, style) => {
        if (!style?.includes(prefix)) {
          return state!;
        }
        return RichUtils.toggleInlineStyle(state!, style);
      }, nextEditorState);
    }

    if (!currentStyle.has(toggledStyle)) {
      nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, toggledStyle);
    }

    return nextEditorState;
  };

  const removePrefixedInlineStyles = (editorState: EditorState, prefix: string) => {
    const selection = editorState.getSelection();

    const nextContentState = Object.keys(styleMap).reduce((contentState, style) => {
      if (!style.startsWith(prefix)) {
        return contentState;
      }
      return Modifier.removeInlineStyle(contentState, selection, style);
    }, editorState.getCurrentContent());

    let nextEditorState = EditorState.push(
      editorState,
      nextContentState,
      "change-inline-style"
    );

    const currentStyle = editorState.getCurrentInlineStyle();

    if (selection.isCollapsed()) {
      nextEditorState = currentStyle.reduce((state, style) => {
        if (!style?.includes(prefix)) {
          return state!;
        }
        return RichUtils.toggleInlineStyle(state!, style);
      }, nextEditorState);
    }

    return nextEditorState;
  };

  const hasPrefixedInlineStyle = (editorState: EditorState, prefix: string) => {
    return editorState.getCurrentInlineStyle().toArray().filter(x => x?.startsWith(prefix)).length > 0;
  };

  const toggleAlignment = (editorState: EditorState, alignment: TextAlignment) => {
    const { content, currentBlock, hasAtomicBlock, target } = getCurrentlySelectedBlock(editorState);

    if (hasAtomicBlock) {
      return editorState;
    }

    const blockData = currentBlock.getData();
    const alignmentToSet = blockData && blockData.get("textAlignment") === alignment ? undefined : alignment;

    const updatedBlockData = Immutable.Map([["textAlignment", alignmentToSet]]);

    return EditorState.push(
      editorState,
      Modifier.mergeBlockData(content, target, updatedBlockData),
      "change-block-data"
    );
  };

  const splitBlock = (editorState: EditorState) => {
    const contentState = Modifier.splitBlock(
      editorState.getCurrentContent(),
      editorState.getSelection()
    );

    const splitState = EditorState.push(editorState, contentState, "split-block");

    const { currentBlock } = getCurrentlySelectedBlock(editorState);

    const alignment = currentBlock.getData().get("textAlignment");

    if (alignment) {
      return toggleAlignment(splitState, alignment)
    } else {
      return splitState;
    }
  };

  useImperativeHandle(ref, () => ({
    getText() {
      return stateToHTML(editorState.getCurrentContent());
    },
    setText(text: string) {
      const contentState = ContentState.createFromText(text);
      setEditorState(EditorState.createWithContent(contentState, decorator));
    },
    getContentState() {
      return convertToRaw(editorState.getCurrentContent());
    },
    setContentState(rawContentState: RawDraftContentState) {
      const contentState = convertFromRaw(rawContentState);
      setEditorState(EditorState.createWithContent(contentState, decorator));
    },
    isEmpty() {
      const contentState = editorState.getCurrentContent();
      const plainText = contentState.getPlainText().trim();
      
      if (plainText === '') {
        return true;
      }
      
      const blocks = contentState.getBlockMap();
      return blocks.every(block => 
        block!.getText().trim() === '' && 
        block!.getType() === 'unstyled'
      );
    }
  }));

  const createStyleToggleFunction = (style: string) => {
    return () => {
      setEditorState(RichUtils.toggleInlineStyle(editorState, style));
      editorRef.current?.focus();
    }
  };

  const createBlockToggleFunction = (style: string) => {
    return () => {
      setEditorState(RichUtils.toggleBlockType(editorState, style));
      editorRef.current?.focus();
    }
  };

  const createToggleAlignmentFunction = (alignment: TextAlignment) => {
    return () => {
      setEditorState(toggleAlignment(editorState, alignment));
      editorRef.current?.focus();
    }
  }

  const createToggleTextColorFunction = (color: string) => {
    return () => {
      setEditorState(togglePrefixedInlineStyle(editorState, "COLOR_", `COLOR_${color}`));
      editorRef.current?.focus();
    }
  }

  const createRemoveTextColorFunction = () => {
    return () => {
      setEditorState(removePrefixedInlineStyles(editorState, "COLOR_"));
      editorRef.current?.focus();
    }
  };

  const createToggleHighlightFunction = (color: string) => {
    return () => {
      setEditorState(togglePrefixedInlineStyle(editorState, "HIGHLIGHT_", `HIGHLIGHT_${color}`));
      editorRef.current?.focus();
    }
  }

  const createRemoveHighlightColorFunction = () => {
    return () => {
      setEditorState(removePrefixedInlineStyles(editorState, "HIGHLIGHT_"));
      editorRef.current?.focus();
    }
  };

  const handleKeyCommand = (command: DraftEditorCommand) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const getCurrentBlock = (editorState: EditorState) => {
    const currentSelection = editorState.getSelection();
    const blockKey = currentSelection.getStartKey();
    return editorState.getCurrentContent().getBlockForKey(blockKey);
  };

  const componentClasses = classNames(
    'w-full h-full rounded-md flex-1',
    'border border-solid border-slate-300 dark:border-slate-800',
    'focus:ring-2 focus:ring-teal-400',
    'bg-slate-50 dark:bg-slate-700'
  );

  const toolbarClasses = classNames(
    'w-full inline-flex flex-row mb-1 p-2',
    'bg-slate-300 dark:bg-slate-600',
    'max-sm:flex-wrap max-sm:gap-y-2'
  );

  const optionsDropdownButtons: DropdownItemProps[] = [
    {
      title: "Headings",
      nestedItems: [
        {
          title: <h1 className="inline-block align-middle">Heading 1</h1>,
          toggleFunction: createBlockToggleFunction("header-one"),
          active: getCurrentBlock(editorState).contains("header-one")
        },
        {
          title: <h2 className="inline-block align-middle">Heading 2</h2>,
          toggleFunction: createBlockToggleFunction("header-two"),
          active: getCurrentBlock(editorState).contains("header-two")
        },
        {
          title: <h3 className="inline-block align-middle">Heading 3</h3>,
          toggleFunction: createBlockToggleFunction("header-three"),
          active: getCurrentBlock(editorState).contains("header-three")
        },
        {
          title: <h4 className="inline-block align-middle">Heading 4</h4>,
          toggleFunction: createBlockToggleFunction("header-four"),
          active: getCurrentBlock(editorState).contains("header-four")
        },
        {
          title: <h5 className="inline-block align-middle">Heading 5</h5>,
          toggleFunction: createBlockToggleFunction("header-five"),
          active: getCurrentBlock(editorState).contains("header-five")
        },
        {
          title: <h6 className="inline-block align-middle">Heading 6</h6>,
          toggleFunction: createBlockToggleFunction("header-six"),
          active: getCurrentBlock(editorState).contains("header-six")
        }
      ]
    },
    {
      title: "Inline",
      nestedItems: [
        {
          title: <strong className="align-middle">Bold</strong>,
          toggleFunction: createStyleToggleFunction("BOLD"),
          active: editorState.getCurrentInlineStyle().contains("BOLD")
        },
        {
          title: <em className="align-middle">Italic</em>,
          toggleFunction: createStyleToggleFunction("ITALIC"),
          active: editorState.getCurrentInlineStyle().contains("ITALIC")
        },
        {
          title: <span className="underline align-middle">Underline</span>,
          toggleFunction: createStyleToggleFunction("UNDERLINE"),
          active: editorState.getCurrentInlineStyle().contains("UNDERLINE")
        },
        {
          title: <span className="line-through align-middle">Strikethrough</span>,
          toggleFunction: createStyleToggleFunction("STRIKETHROUGH"),
          active: editorState.getCurrentInlineStyle().contains("STRIKETHROUGH")
        }
      ]
    },
    {
      title: "Blocks",
      nestedItems: [
        {
          title: "Paragraph",
          toggleFunction: createBlockToggleFunction("unstyled"),
          active: getCurrentBlock(editorState).contains("unstyled")
        },
        {
          title: "Blockquote",
          toggleFunction: createBlockToggleFunction("blockquote"),
          active: getCurrentBlock(editorState).contains("blockquote")
        }
      ]
    },
    {
      title: "Align",
      nestedItems: [
        {
          title: "Left",
          toggleFunction: createToggleAlignmentFunction("left"),
          active: getCurrentBlock(editorState).getData().get("textAlignment") === "left"
        },
        {
          title: "Center",
          toggleFunction: createToggleAlignmentFunction("center"),
          active: getCurrentBlock(editorState).getData().get("textAlignment") === "center"
        },
        {
          title: "Right",
          toggleFunction: createToggleAlignmentFunction("right"),
          active: getCurrentBlock(editorState).getData().get("textAlignment") === "right"
        },
        {
          title: "Justify",
          toggleFunction: createToggleAlignmentFunction("justify"),
          active: getCurrentBlock(editorState).getData().get("textAlignment") === "justify"
        }
      ]
    }
  ];

  const optionsDropdownButtonsSimple: DropdownItemProps[] = [
    {
      title: "Headings",
      nestedItems: [
        {
          title: <h1 className="inline-block align-middle">Heading 1</h1>,
          toggleFunction: createBlockToggleFunction("header-one"),
          active: getCurrentBlock(editorState).contains("header-one")
        },
        {
          title: <h2 className="inline-block align-middle">Heading 2</h2>,
          toggleFunction: createBlockToggleFunction("header-two"),
          active: getCurrentBlock(editorState).contains("header-two")
        },
        {
          title: <h3 className="inline-block align-middle">Heading 3</h3>,
          toggleFunction: createBlockToggleFunction("header-three"),
          active: getCurrentBlock(editorState).contains("header-three")
        },
        {
          title: <h4 className="inline-block align-middle">Heading 4</h4>,
          toggleFunction: createBlockToggleFunction("header-four"),
          active: getCurrentBlock(editorState).contains("header-four")
        },
        {
          title: <h5 className="inline-block align-middle">Heading 5</h5>,
          toggleFunction: createBlockToggleFunction("header-five"),
          active: getCurrentBlock(editorState).contains("header-five")
        },
        {
          title: <h6 className="inline-block align-middle">Heading 6</h6>,
          toggleFunction: createBlockToggleFunction("header-six"),
          active: getCurrentBlock(editorState).contains("header-six")
        }
      ]
    },
    {
      title: "Inline",
      nestedItems: [
        {
          title: <strong className="align-middle">Bold</strong>,
          toggleFunction: createStyleToggleFunction("BOLD"),
          active: editorState.getCurrentInlineStyle().contains("BOLD")
        },
        {
          title: <em className="align-middle">Italic</em>,
          toggleFunction: createStyleToggleFunction("ITALIC"),
          active: editorState.getCurrentInlineStyle().contains("ITALIC")
        },
        {
          title: <span className="underline align-middle">Underline</span>,
          toggleFunction: createStyleToggleFunction("UNDERLINE"),
          active: editorState.getCurrentInlineStyle().contains("UNDERLINE")
        },
        {
          title: <span className="line-through align-middle">Strikethrough</span>,
          toggleFunction: createStyleToggleFunction("STRIKETHROUGH"),
          active: editorState.getCurrentInlineStyle().contains("STRIKETHROUGH")
        }
      ]
    },
    {
      title: "Blocks",
      nestedItems: [
        {
          title: "Paragraph",
          toggleFunction: createBlockToggleFunction("unstyled"),
          active: getCurrentBlock(editorState).contains("unstyled")
        },
        {
          title: "Blockquote",
          toggleFunction: createBlockToggleFunction("blockquote"),
          active: getCurrentBlock(editorState).contains("blockquote")
        }
      ]
    },
  ];

  const editorButtonClasses = width <= 700 ? "hidden" : ""; // Responsive design for editor

  if (simple) {
    return (
      <div className={componentClasses} ref={containerRef}>
        <div className={toolbarClasses}>
          <Dropdown title="Options" buttons={optionsDropdownButtonsSimple} open={optionsDropdownOpen} setOpen={setOptionsDropdownOpen} />
          <EditorButton toggleFunction={createStyleToggleFunction('BOLD')} className={editorButtonClasses}
            active={editorState.getCurrentInlineStyle().contains('BOLD')}>
            <span className="mx-auto font-bold text-lg">B</span>
          </EditorButton>
          <EditorButton toggleFunction={createStyleToggleFunction('ITALIC')} className={editorButtonClasses}
            active={editorState.getCurrentInlineStyle().contains('ITALIC')}>
            <span className="mx-auto italic text-lg">I</span>
          </EditorButton>
          <EditorButton toggleFunction={createStyleToggleFunction('UNDERLINE')} className={editorButtonClasses}
            active={editorState.getCurrentInlineStyle().contains('UNDERLINE')}>
            <span className="mx-auto underline text-lg">U</span>
          </EditorButton>
          <EditorButton toggleFunction={createBlockToggleFunction('unordered-list-item')} className={editorButtonClasses}
            active={getCurrentBlock(editorState).contains('unordered-list-item')}>
            <span className="mx-auto text-lg">
              <ListUl />
            </span>
          </EditorButton>
          <EditorButton toggleFunction={createBlockToggleFunction('ordered-list-item')} className={editorButtonClasses}
            active={getCurrentBlock(editorState).contains('ordered-list-item')}>
            <span className="mx-auto text-lg">
              <ListOl />
            </span>
          </EditorButton>
          <LinkEditor
            open={linkEditorOpen}
            setOpen={setLinkEditorOpen}
            editorState={editorState}
            setEditorState={setEditorState}
          />
        </div>
        <div className="p-2 h-full prose dark:prose-invert cursor-text" id="rich-text-editor" onClick={() => { editorRef.current?.focus(); }}>
          <Editor editorState={editorState} onChange={setEditorState} handleKeyCommand={handleKeyCommand} ref={editorRef}
            customStyleMap={styleMap} blockStyleFn={getBlockStyle} spellCheck autoCorrect="on"
            handleReturn={() => {
              setEditorState(splitBlock(editorState)); // Allows text alignment to carry to the next line after pressing enter (can also change alignments on lines)
              return "handled";
            }} />
        </div>
      </div>
    );
  } else {
    return (
      <div className={componentClasses} ref={containerRef}>
        <div className={toolbarClasses}>
          <Dropdown title="Options" buttons={optionsDropdownButtons} open={optionsDropdownOpen} setOpen={setOptionsDropdownOpen} />
          <EditorButton toggleFunction={createStyleToggleFunction('BOLD')} className={editorButtonClasses}
            active={editorState.getCurrentInlineStyle().contains('BOLD')}>
            <span className="mx-auto font-bold text-lg">B</span>
          </EditorButton>
          <EditorButton toggleFunction={createStyleToggleFunction('ITALIC')} className={editorButtonClasses}
            active={editorState.getCurrentInlineStyle().contains('ITALIC')}>
            <span className="mx-auto italic text-lg">I</span>
          </EditorButton>
          <EditorButton toggleFunction={createStyleToggleFunction('UNDERLINE')} className={editorButtonClasses}
            active={editorState.getCurrentInlineStyle().contains('UNDERLINE')}>
            <span className="mx-auto underline text-lg">U</span>
          </EditorButton>
          <EditorButton toggleFunction={createBlockToggleFunction('unordered-list-item')} className={editorButtonClasses}
            active={getCurrentBlock(editorState).contains('unordered-list-item')}>
            <span className="mx-auto text-lg">
              <ListUl />
            </span>
          </EditorButton>
          <EditorButton toggleFunction={createBlockToggleFunction('ordered-list-item')} className={editorButtonClasses}
            active={getCurrentBlock(editorState).contains('ordered-list-item')}>
            <span className="mx-auto text-lg">
              <ListOl />
            </span>
          </EditorButton>
          <EditorButton toggleFunction={createToggleAlignmentFunction("left")} className={editorButtonClasses}
            active={getCurrentBlock(editorState).getData().get("textAlignment") === "left"}>
            <span className="mx-auto text-lg">
              <TextLeft />
            </span>
          </EditorButton>
          <EditorButton toggleFunction={createToggleAlignmentFunction("center")} className={editorButtonClasses}
            active={getCurrentBlock(editorState).getData().get("textAlignment") === "center"}>
            <span className="mx-auto text-lg">
              <TextCenter />
            </span>
          </EditorButton>
          <EditorButton toggleFunction={createToggleAlignmentFunction("right")} className={editorButtonClasses}
            active={getCurrentBlock(editorState).getData().get("textAlignment") === "right"}>
            <span className="mx-auto text-lg">
              <TextRight />
            </span>
          </EditorButton>
          <EditorButton toggleFunction={createToggleAlignmentFunction("justify")} className={editorButtonClasses}
            active={getCurrentBlock(editorState).getData().get("textAlignment") === "justify"}>
            <span className="mx-auto text-lg">
              <Justify />
            </span>
          </EditorButton>
          <LinkEditor
            open={linkEditorOpen}
            setOpen={setLinkEditorOpen}
            editorState={editorState}
            setEditorState={setEditorState}
          />
        </div>
        <div className="p-2 h-full prose dark:prose-invert cursor-text" id="rich-text-editor" onClick={() => { editorRef.current?.focus(); }}>
          <Editor editorState={editorState} onChange={setEditorState} handleKeyCommand={handleKeyCommand} ref={editorRef}
            customStyleMap={styleMap} blockStyleFn={getBlockStyle} spellCheck autoCorrect="on"
            handleReturn={() => {
              setEditorState(splitBlock(editorState)); // Allows text alignment to carry to the next line after pressing enter (can also change alignments on lines)
              return "handled";
            }} />
        </div>
      </div>
    );
  }
});

declare interface EditorButtonProps extends AppProps {
  toggleFunction: () => void;
  active: boolean;
}

function EditorButton({ active, children, toggleFunction, ...props }: EditorButtonProps): JSX.Element {
  const componentClasses = classNames(
    'mx-1 w-8 h-8 rounded-2xl',
    { 'bg-slate-100 dark:bg-slate-800': !active },
    { 'bg-usfTeal': active },
    'inline-flex items-center align-middle text-center',
    'hover:scale-125 hover:cursor-pointer',
    props.className
  );

  return (
    <a className={componentClasses} onMouseDown={e => {
      e.preventDefault();
      toggleFunction();
    }}>
      {children}
    </a>
  );
}
